import theme from "./theme";
import classNames from "classnames";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { ListItemNode, ListNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import "@/lib/lexical/css/CheckList.css";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { EditorState, LexicalEditor, ParagraphNode, $createParagraphNode, $createTextNode, $getRoot } from "lexical";
import { AlignmentPlugin } from "@/components/RichTextEditor/plugins/AlignmentPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import { initEditorWithHtml, handleEditorChangeToHtml, decodeHtmlEntities } from "@/lib/htmlHelpers";
import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { COMMAND_PRIORITY_LOW, PASTE_COMMAND, $getSelection, $insertNodes } from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import ImagesPlugin from "@/lib/lexical/plugins/ImagesPlugin";
import { ImageNode } from "@/lib/lexical/nodes/ImageNode";
import { InlineImageNode } from "@/lib/lexical/nodes/InlineImageNode";

export interface RichTextEditorProps {
  placeholder?: string;
  hasOverlayButton?: boolean;
  onInitialState?: (editor: LexicalEditor) => void;
  onChange?: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void;
  error?: string;
  initialContent?: string;
  label?: string;
  enableHtmlFeatures?: boolean;
  enableImageUpload?: boolean;
}

function Placeholder({ placeholder = "Description..." }: { placeholder?: string }) {
  return (
    <div className="editor-placeholder absolute top-3 left-3 text-body-large text-sys-brand-on-surface-variant pointer-events-none">
      {placeholder}
    </div>
  );
}

// Custom plugin to handle paste events for better HTML processing
function PasteLogPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) {
          return false;
        }

        // Get both HTML and plain text
        let html = clipboardData.getData("text/html");
        let text = clipboardData.getData("text/plain");

        // Replace all U+FFFD (replacement character) with a space
        // We only want to strip the actual replacement character () while leaving the rest of the
        // string intact. The previous pattern erroneously included an empty alternative ("|"), which
        // could lead to unintended matches.
        const clean = (str: string) => (str ? str.replace(/\uFFFD/g, "") : str);
        html = clean(html);
        text = clean(text);
        if (html) {
          event.preventDefault();

          editor.update(() => {
            try {
              const decodedHtml = decodeHtmlEntities(html);

              const parser = new DOMParser();
              const dom = parser.parseFromString(decodedHtml, "text/html");

              const nodes = $generateNodesFromDOM(editor, dom);
              const selection = $getSelection();
              if (selection) {
                $insertNodes(nodes);
              }
            } catch (e) {
              console.error("Error processing pasted HTML:", e);
            }
          });
          return true;
        } else if (text) {
          event.preventDefault();
          editor.update(() => {
            try {
              // Insert as plain text, replacing U+FFFD
              const paragraph = $createParagraphNode();
              const textNode = $createTextNode(text);
              paragraph.append(textNode);
              $getRoot().append(paragraph);
            } catch (e) {
              console.error("Error processing pasted text:", e);
            }
          });
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
}

export default function RichTextEditor({
  placeholder,
  onInitialState,
  onChange,
  hasOverlayButton,
  error,
  initialContent,
  label,
  enableHtmlFeatures = false,
  enableImageUpload = false
}: RichTextEditorProps) {
  const editorConfig = {
    namespace: "Editor",
    theme: theme,
    editorState: (editor: LexicalEditor) => {
      initEditorWithHtml(editor, initialContent);
      onInitialState?.(editor);
    },
    onError: (error: Error) => {
      throw error;
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      ParagraphNode,
      CodeNode,
      CodeHighlightNode,
      QuoteNode,
      ...(enableImageUpload ? [ImageNode, InlineImageNode] : [])
    ],
    html: {
      import: {
        div: (_domNode: HTMLElement) => {
          return {
            conversion: (element: HTMLElement) => {
              // Refined logic to check if the div contains any non-inline children.
              // If it only contains inline content or is empty, it can be converted to a paragraph.
              // Otherwise, it should be unwrapped (node: null) to let Lexical process its children.
              let hasNonInlineChild = false;
              const inlineTagNames = [
                "span",
                "strong",
                "em",
                "u",
                "a",
                "b",
                "i",
                "br",
                ...(enableImageUpload ? ["img"] : [])
              ];

              if (element.childNodes.length > 0) {
                for (const childNode of Array.from(element.childNodes)) {
                  if (childNode.nodeType === 1) {
                    // Node.ELEMENT_NODE
                    const childElement = childNode as HTMLElement;
                    const tagName = childElement.tagName.toLowerCase();
                    if (!inlineTagNames.includes(tagName)) {
                      // If it's an element not in our inline list, consider it non-inline.
                      hasNonInlineChild = true;
                      break;
                    }
                  } else if (childNode.nodeType === 3 && childNode.textContent?.trim() !== "") {
                    // Node.TEXT_NODE
                    // Non-empty text nodes are considered inline content.
                  } else if (
                    childNode.nodeType !== 3 &&
                    childNode.nodeType !== 1 &&
                    childNode.nodeType !== 8 &&
                    childNode.textContent?.trim() !== ""
                  ) {
                    // Other node types with content (excluding comments) might indicate non-inline structure.
                    // For now, primarily focusing on elements and text nodes.
                  }
                }
              }

              if (hasNonInlineChild) {
                // If the DIV contains any child that is not considered inline,
                // unwrap this DIV and let Lexical process its children directly.
                return { node: null };
              } else {
                // If the DIV seems to only wrap inline content or is empty,
                // convert it to a ParagraphNode. Lexical's $generateNodesFromDOM
                // will then populate this paragraph with the div's children.
                return { node: $createParagraphNode() };
              }
            },
            priority: 1 as const
          };
        }
      }
    }
  };

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    if (onChange) {
      handleEditorChangeToHtml(editorState, (html) => {
        onChange(editorState, editor, new Set([html]));
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <div className="text-title-medium text-sys-brand-on-surface">{label}</div>}
      <LexicalComposer initialConfig={editorConfig}>
        <div
          className={classNames("editor-container border border-sys-brand-outline-variant h-full rounded", {
            "border !border-sys-brand-error": error
          })}
        >
          <ToolbarPlugin enableHtmlFeatures={enableHtmlFeatures} enableImageUpload={enableImageUpload} />
          <HistoryPlugin />
          <div className="editor-inner p-3 relative text-body-large">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={classNames(
                    "editor-input antialiased min-h-[150px] max-h-[300px] overflow-y-auto !outline-none",
                    {
                      "mb-16": hasOverlayButton
                    }
                  )}
                  data-testid="editor-input"
                />
              }
              placeholder={<Placeholder placeholder={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ListPlugin />
            <CheckListPlugin />
            <LinkPlugin />
            {enableHtmlFeatures && <AlignmentPlugin />}
            {enableImageUpload && <ImagesPlugin />}
            {onChange && <OnChangePlugin onChange={handleChange} />}
            <PasteLogPlugin />
          </div>
        </div>
        {error && <div className="text-sys-brand-error text-body-small ml-4 pt-1">{error}</div>}
      </LexicalComposer>
    </div>
  );
}
